import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Draft, MeResponse, Member, Message, RoomState } from '@cs/shared'
import { api, ApiError, clearToken, establishSession, getToken, openStream } from './api.js'
import { Join } from './views/Join.js'
import { Timeline } from './views/Timeline.js'
import { Approvals } from './views/Approvals.js'
import { Specs } from './views/Specs.js'
import { Files } from './views/Files.js'
import { Board } from './views/Board.js'
import { Admin } from './views/Admin.js'
import { Home } from './views/Home.js'
import { Help } from './views/Help.js'

export type Tab = 'board' | 'timeline' | 'approvals' | 'specs' | 'members'

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'board', label: '現状' },
  { id: 'timeline', label: 'チャット' },
  { id: 'approvals', label: '共有待ち' },
  // Renamed from 仕様書: in practice this is where every kind of file ends up —
  // progress reports, scripts, screenshots — and calling it 仕様書 made people
  // hesitate before putting the thing they actually had somewhere.
  { id: 'specs', label: 'ファイル' },
  // Visible to everyone: who decides what is something the whole room needs to see,
  // even though only an owner can change it.
  { id: 'members', label: 'メンバー' },
]

/**
 * Where you are, in the URL.
 *
 * `#/` is the project list, `#/help` the manual, `#/r/<id>/<tab>` a project. Putting
 * the project in the address rather than in a dropdown means reloading, bookmarking
 * and pasting a link all land where you expect — and it is what lets the landing
 * page exist at all, since there is now somewhere to be that is not inside a
 * project.
 */
type Route =
  | { kind: 'home' }
  | { kind: 'help' }
  | { kind: 'room'; room: string; tab: Tab }

function readRoute(): Route {
  const parts = location.hash.replace(/^#\/?/, '').split('/').filter(Boolean)
  if (parts[0] === 'help') return { kind: 'help' }
  if (parts[0] === 'r' && parts[1]) {
    const tab = (TABS.find((t) => t.id === parts[2])?.id ?? 'board') as Tab
    return { kind: 'room', room: parts[1], tab }
  }
  return { kind: 'home' }
}

/** Drop `?code=` once it has been used, so a reload cannot try it a second time. */
function clearInviteFromUrl(): void {
  const url = new URL(location.href)
  url.searchParams.delete('code')
  history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
}

function hashFor(route: Route): string {
  if (route.kind === 'help') return '#/help'
  if (route.kind === 'room') return `#/r/${route.room}/${route.tab}`
  return '#/'
}

export function App() {
  const [me, setMe] = useState<MeResponse | null>(null)
  const [booting, setBooting] = useState(true)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [consentCode, setConsentCode] = useState<string | null>(null)
  const [route, setRoute] = useState<Route>(readRoute)
  const room = route.kind === 'room' ? route.room : null
  const tab = route.kind === 'room' ? route.tab : 'board'

  const [messages, setMessages] = useState<Message[]>([])
  const [latestSeq, setLatestSeq] = useState(0)
  const [drafts, setDrafts] = useState<Draft[]>([])
  const [state, setState] = useState<RoomState | null>(null)
  const [connected, setConnected] = useState(false)
  const [webVersion, setWebVersion] = useState<string | null>(null)
  const [stale, setStale] = useState(false)

  // The stream callback must not be re-created on every message, so the current seq
  // is read through a ref instead of being captured in the closure.
  const seqRef = useRef(0)
  seqRef.current = latestSeq

  useEffect(() => {
    const onHash = () => setRoute(readRoute())
    addEventListener('hashchange', onHash)
    return () => removeEventListener('hashchange', onHash)
  }, [])

  const navigate = useCallback((next: Route) => {
    location.hash = hashFor(next)
    setRoute(next)
  }, [])

  useEffect(() => {
    // Before anything else: the stream authenticates by cookie, and a member who
    // joined from the CLI has only a token in localStorage.
    void establishSession()

    /*
     * An invite link is `/join?code=…`, and it has to work for somebody who is
     * already here.
     *
     * That is the normal case for this system, not the edge one: the same people run
     * several projects together, so the second invite always arrives at a browser
     * that already holds a token. Previously the code was only read on the sign-in
     * screen, which that person never sees — so the link silently did nothing and
     * they were left looking at the projects they already had.
     *
     * Redeeming with the token attached adds the project to the existing identity
     * rather than minting a second one. Consent is not re-asked: it is about the
     * system, not about one room, and the hub says so if the text has changed since.
     */
    const code = new URLSearchParams(location.search).get('code')

    const boot = async () => {
      let result: MeResponse | null = null
      try {
        result = await api.me()
      } catch {
        result = null
      }

      if (code && result) {
        try {
          const joined = await api.redeem(code)
          clearInviteFromUrl()
          result = await api.me()
          setMe(result)
          setRoute({ kind: 'room', room: joined.room.id, tab: 'board' })
          setBooting(false)
          return
        } catch (err) {
          clearInviteFromUrl()
          if (err instanceof ApiError && err.code === 'consent_required') {
            // The code is fine; the rules have changed since they last accepted.
            // Send them to the consent screen with the code already filled in.
            setConsentCode(code)
          } else {
            // Expired, already used, revoked. Say which instead of dropping them on
            // an unexplained list of the projects they already had.
            setInviteError(describeError(err))
          }
        }
      }

      setMe(result)
      if (result) {
        // A project in the URL that this person is no longer in must not linger:
        // send them to the list rather than to a 404 inside a room.
        setRoute((r) =>
          r.kind === 'room' && !result!.rooms.some((x) => x.id === r.room) ? { kind: 'home' } : r,
        )
      }
      setBooting(false)
    }

    void boot()
  }, [])

  const refresh = useCallback(async () => {
    if (!room) return
    const [msgs, queue, roomState] = await Promise.all([
      api.messages(room),
      api.drafts(room, 'pending'),
      api.state(room),
    ])
    setMessages(msgs.messages)
    setLatestSeq(msgs.latest_seq)
    setDrafts(queue.drafts)
    setState(roomState)
  }, [room])

  useEffect(() => {
    if (!room) return
    // Switching project must not show the previous one's content for even a frame.
    setMessages([])
    setDrafts([])
    setState(null)
    setLatestSeq(0)
    void refresh()
  }, [room, refresh])

  useEffect(() => {
    if (!room) return
    return openStream(
      room,
      () => seqRef.current,
      (event) => {
        switch (event.type) {
          case 'message':
            setMessages((prev) =>
              prev.some((m) => m.id === event.message.id) ? prev : [...prev, event.message],
            )
            setLatestSeq((prev) => Math.max(prev, event.message.seq))
            break
          case 'message_retracted':
            setMessages((prev) =>
              event.message
                ? prev.map((m) => (m.id === event.message_id ? event.message! : m))
                : prev.filter((m) => m.id !== event.message_id),
            )
            break
          case 'report_retracted':
            void api.state(room).then(setState)
            break
          case 'hello':
            // A changed build means this tab is running superseded JavaScript.
            setWebVersion((prev) => {
              if (event.web_version && prev && prev !== event.web_version) setStale(true)
              return event.web_version ?? prev
            })
            // Reconnects can straddle a gap the replay does not cover.
            void refresh()
            break
          case 'message_resolved':
            setMessages((prev) =>
              prev.map((m) =>
                m.id === event.message_id
                  ? { ...m, resolved_at: new Date().toISOString(), resolved_by: event.resolved_by }
                  : m,
              ),
            )
            break
          case 'draft':
            setDrafts((prev) => {
              // Drop the one this replaced as well as the one being updated: a
              // superseded draft no longer exists on the hub, and approving a card
              // for a deleted row is where "下書きが見つかりません" comes from.
              const without = prev.filter(
                (d) => d.id !== event.draft.id && d.id !== event.superseded,
              )
              return event.draft.status === 'pending' ? [event.draft, ...without] : without
            })
            break
          case 'report':
          case 'doc':
          case 'decision':
            // These change the board rather than the timeline; refetch it lazily.
            void api.state(room).then(setState)
            break
        }
      },
      setConnected,
    )
  }, [room, refresh])

  const current = me?.rooms.find((r) => r.id === room) ?? null

  /**
   * The person as they stand in *this* project: identity is global, role is not.
   * The room's member list is authoritative once loaded; until then this is built
   * from `/v1/me`, which carries permission and settings but not responsibility.
   */
  const member: Member | null = useMemo(() => {
    if (!me || !current) return null
    const fromRoom = state?.members.find((m) => m.user_id === me.person.person_id)
    if (fromRoom) return fromRoom
    return {
      user_id: me.person.person_id,
      display_name: me.person.display_name,
      org: me.person.org,
      agent_nickname: me.person.agent_nickname,
      role: current.role,
      authorities: [],
      settings: current.settings,
      agent_online: false,
      agent_last_seen_at: me.person.agent_last_seen_at,
      created_at: me.person.created_at,
      last_seen_at: me.person.last_seen_at,
      // Unknown until the room state arrives; the board reads it from there.
      activity: { worked: 'never', shared: 'never', unshared: false },
    }
  }, [me, current, state])

  const go = (next: Tab) => {
    if (room) navigate({ kind: 'room', room, tab: next })
  }

  if (booting) return <div className="center muted">読み込み中…</div>
  if (!me) return <Join onJoined={() => location.reload()} />
  if (consentCode) {
    return (
      <div className="center">
        <div>
          <div className="notice">
            このプロジェクトに参加するには、参加条件への同意が必要です。
          </div>
          <Join code={consentCode} onJoined={() => location.reload()} />
        </div>
      </div>
    )
  }

  if (me.rooms.length === 0) {
    return (
      <div className="center">
        <div className="card">
          <h2>参加しているプロジェクトがありません</h2>
          <p className="muted">
            {me.person.display_name} さんとしてログインしていますが、まだどのプロジェクトにも
            参加していません。招待コードを受け取ったら、下の画面から参加してください。
          </p>
          <Join onJoined={() => location.reload()} />
        </div>
      </div>
    )
  }

  if (route.kind === 'help') {
    return (
      <div className="app">
        <Bar me={me} onHome={() => navigate({ kind: 'home' })} />
        <main>
          <Help onBack={() => navigate({ kind: 'home' })} />
        </main>
      </div>
    )
  }

  if (!room || !current || !member) {
    return (
      <div className="app">
        <Bar me={me} onHome={() => navigate({ kind: 'home' })} />
        <main>
          {inviteError && (
            <div className="wrap" style={{ paddingBottom: 0 }}>
              <div className="error">
                招待リンクを開けませんでした: {inviteError}
                <button className="text" onClick={() => setInviteError(null)}>
                  閉じる
                </button>
              </div>
            </div>
          )}
          <Home
            me={me}
            onOpen={(id) => navigate({ kind: 'room', room: id, tab: 'board' })}
            onHelp={() => navigate({ kind: 'help' })}
          />
        </main>
      </div>
    )
  }

  return (
    <div className="app">
      <header className="topbar">
        <button className="brand" title="プロジェクト一覧へ" onClick={() => navigate({ kind: 'home' })}>
          文殊
        </button>
        <button className="text" onClick={() => navigate({ kind: 'home' })} title="プロジェクト一覧">
          ←
        </button>
        <span className="room-name">{current.name}</span>
        <nav className="tabs">
          {TABS.map((t) => (
            <button key={t.id} aria-current={tab === t.id} onClick={() => go(t.id)}>
              {t.label}
              {t.id === 'approvals' && drafts.length > 0 ? (
                <span className="badge">{drafts.length}</span>
              ) : null}
            </button>
          ))}
        </nav>
        <span className="whoami">
          <button className="text" onClick={() => navigate({ kind: 'help' })}>
            使い方
          </button>
          {me.person.display_name} / {me.person.org}
          <button
            className="text"
            onClick={() => {
              clearToken()
              location.reload()
            }}
          >
            切断
          </button>
        </span>
      </header>

      <main>
        <div className="wrap">
          {stale && (
            /* The page is rendering data from a newer hub than the code it is
               running. That combination is what made retracted messages look like
               blank rows, so it is worth interrupting for. */
            <div className="notice reload-banner">
              <strong>新しいバージョンが公開されています。</strong>{' '}
              このページは古いままなので、表示が正しくないことがあります。
              <button className="primary" style={{ marginLeft: 10 }} onClick={() => location.reload()}>
                再読み込み
              </button>
            </div>
          )}
          {!connected && (
            <div className="notice">
              リアルタイム更新に接続できていません。再接続を試みています…
              （この間、新しい発言は自動では表示されません）
            </div>
          )}
          {tab === 'board' && <Board
              state={state}
              onRefresh={refresh}
              room={room}
              meId={me.person.person_id}
              canEdit={member.role !== 'viewer'}
            />}
          {tab === 'timeline' && (
            <Timeline
              room={room}
              me={member}
              messages={messages}
              members={state?.members ?? []}
              onChanged={refresh}
            />
          )}
          {tab === 'approvals' && (
            <Approvals
              room={room}
              drafts={drafts}
              members={state?.members ?? []}
              meId={me.person.person_id}
              onChanged={refresh}
            />
          )}
          {tab === 'specs' && (
            <>
              <Specs room={room} me={member} onChanged={refresh} />
              <Files
                room={room}
                files={state?.files ?? []}
                me={member}
                canEdit={member.role !== 'viewer'}
                onChanged={refresh}
              />
            </>
          )}
          {tab === 'members' && (
            <Admin room={room} me={member} state={state} onChanged={refresh} />
          )}
        </div>
      </main>
    </div>
  )
}

/** Shared by the views: turn an ApiError into something a person can act on. */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return String(err)
}

export function useHasToken(): boolean {
  return useMemo(() => getToken() !== null, [])
}


/** The app bar outside a project: identity and a way back, nothing else. */
function Bar({ me, onHome }: { me: MeResponse; onHome: () => void }) {
  return (
    <header className="topbar">
      <button className="brand" title="プロジェクト一覧へ" onClick={onHome}>
        文殊
      </button>
      <span className="whoami">
        {me.person.display_name} / {me.person.org}
        <button
          className="text"
          onClick={() => {
            clearToken()
            location.reload()
          }}
        >
          切断
        </button>
      </span>
    </header>
  )
}
